// Dynamic entity-setup registry (see AUTH_LOGIN_FLOW_PLAN.md §3-§6). No role names here —
// `frontendServiceKey` feeds hasServiceAccess() (accessControls-backed, live), and
// `entityFields` lists every fullUserData field that counts as "this service's entity exists"
// for ANY tier of role that can hold it (admin-tier fpoEntity/companyEntity, member-tier
// membershipEntity) — AuthContext.pendingEntitySetup checks `some()` over these, not a single
// hardcoded role→field pairing.
export const SERVICE_ENTITY_REGISTRY = {
  MY_ORG: {
    serviceKey: "MY_ORG",
    frontendServiceKey: "myFpo",
    entityFields: ["fpoEntity", "membershipEntity"],
    routePrefix: "/myFpo",
    setupRoute: "/myFpo/setup",
    overviewRoute: "/myFpo",
    label: "Org",
    // The usertype this service is registered/approved under (see
    // AdminUserInitializer.createSystemUserTypes / CompanyServiceImpl.createUser's "Company/
    // Business accounts are Individual, not Institutional" note). Used by ServiceGuard's
    // cross-service (Case 2) redirect: a service whose home usertype differs from the visiting
    // account's own is a separate registration/subscription, not an upgrade of their own plan.
    homeUserTypeCode: "INSTITUTIONAL",
  },

  MY_BUSINESS: {
    serviceKey: "MY_BUSINESS",
    frontendServiceKey: "mybusiness",
    // "membershipEntity" deliberately excluded, unlike MY_ORG's below - fullUserData comes
    // exclusively from fpo's /users/{id} (getUsersId), and fpo's own MembershipEntity
    // (backend model, fpoName field, no company relation at all) can only ever represent FPO
    // membership - there is currently no equivalent field surfacing a mybusiness-side company
    // membership through fullUserData at all. Confirmed live: an FPO member's genuine
    // membershipEntity (correctly theirs, for MY_ORG) made hasExistingEntity here wrongly true
    // for MY_BUSINESS too, so ServiceGuard's cross-usertype check skipped straight past the real
    // "/mybusiness/setup" Company registration form to the generic plan carousel instead - the
    // member could request and get approved for a plan that granted them no actual MyBusiness
    // access at all, since no companyEntity or business role was ever created.
    entityFields: ["companyEntity"],
    routePrefix: "/mybusiness",
    setupRoute: "/mybusiness/setup",
    overviewRoute: "/mybusiness",
    label: "Company",
    homeUserTypeCode: "INDIVIDUAL",
  },
};

// Which member usertype can genuinely hold each service's member-tier entity field
// ("membershipEntity" specifically). Mirrors feed's AccessEvaluationServiceImpl.MEMBER_HOME_SERVICE
// / MEMBER_TENANT_USERTYPE (also duplicated in SubscriptionPage.jsx as MEMBER_TENANT_USERTYPE).
export const SERVICE_MEMBER_USERTYPE = {
  MY_ORG: "FPO_MEMBER",
  MY_BUSINESS: "BUSINESS_MEMBER",
};

// True only when this entityFields value is trustworthy evidence of a genuine entity for this
// service. "membershipEntity" is ambiguous by construction - fpo's UserServiceImpl.
// applyMembershipTenantId() populates it from a BUSINESS_MEMBER's own mybusiness membership as a
// fallback (so that member's own accounts-page data fetches resolve correctly), not just from a
// genuine fpo MembershipEntity - so its mere presence only proves genuine MY_ORG/MY_BUSINESS
// membership when the account's own usertype actually matches this service's real member usertype
// (SERVICE_MEMBER_USERTYPE above). Confirmed live 2026-09-18: a BUSINESS_MEMBER's
// fullUserData.membershipEntity held her own mybusiness_membership row (tenantId = a company id),
// with fpoEntity/companyEntity both undefined - entityFields.some(...) alone read that as "already
// has a MY_ORG entity", which is only harmless today because ServiceGuard's SUBSCRIBE_REQUIRED
// branch happens to converge on the subscribe picker either way; every entityFields.some() call
// site should still gate through this so that convergence isn't the only thing protecting it.
export function isGenuineEntityField(serviceKey, field, userTypeCode) {
  if (field !== "membershipEntity") return true;
  return userTypeCode === SERVICE_MEMBER_USERTYPE[serviceKey];
}
